/**
 * Service Worker 디버깅 유틸리티
 * 푸시 알림 수신 상태를 확인하고 로그를 조회하는 도구
 */

export interface PushReceiveLog {
  receivedAt: string
  hasData: boolean
  rawData: string | null
  parsed?: boolean
  title?: string
  body?: string
  parseError?: string
  displayed?: boolean
  displayedAt?: string
  displayError?: string
  error?: string
}

export interface ServiceWorkerDebugInfo {
  isActive: boolean
  scope?: string
  scriptURL?: string
  state?: string
  updateFound: boolean
  lastUpdate?: string
}

class ServiceWorkerDebugger {
  /**
   * Service Worker 푸시 수신 로그 조회
   */
  async getPushLogs(): Promise<PushReceiveLog[]> {
    try {
      const registration = await navigator.serviceWorker.getRegistration()
      if (!registration || !registration.active) {
        console.warn('[Debug] No active service worker found')
        return []
      }

      return new Promise((resolve, reject) => {
        const messageChannel = new MessageChannel()

        messageChannel.port1.onmessage = (event) => {
          if (event.data.success) {
            resolve(event.data.logs || [])
          } else {
            reject(new Error(event.data.message || 'Failed to get push logs'))
          }
        }

        registration.active.postMessage(
          { type: 'GET_PUSH_LOG' },
          [messageChannel.port2]
        )

        // 타임아웃 설정 (5초)
        setTimeout(() => {
          reject(new Error('Service Worker response timeout'))
        }, 5000)
      })
    } catch (error) {
      console.error('[Debug] Failed to get push logs:', error)
      throw error
    }
  }

  /**
   * Service Worker 푸시 수신 로그 초기화
   */
  async clearPushLogs(): Promise<void> {
    try {
      const registration = await navigator.serviceWorker.getRegistration()
      if (!registration || !registration.active) {
        console.warn('[Debug] No active service worker found')
        return
      }

      return new Promise((resolve, reject) => {
        const messageChannel = new MessageChannel()

        messageChannel.port1.onmessage = (event) => {
          if (event.data.success) {
            console.log('[Debug] Push logs cleared')
            resolve()
          } else {
            reject(new Error(event.data.message || 'Failed to clear push logs'))
          }
        }

        registration.active.postMessage(
          { type: 'CLEAR_PUSH_LOG' },
          [messageChannel.port2]
        )

        // 타임아웃 설정 (5초)
        setTimeout(() => {
          reject(new Error('Service Worker response timeout'))
        }, 5000)
      })
    } catch (error) {
      console.error('[Debug] Failed to clear push logs:', error)
      throw error
    }
  }

  /**
   * Service Worker 활성 상태 확인 (PING)
   */
  async pingServiceWorker(): Promise<{ active: boolean; timestamp?: string }> {
    try {
      const registration = await navigator.serviceWorker.getRegistration()
      if (!registration || !registration.active) {
        return { active: false }
      }

      return new Promise((resolve, reject) => {
        const messageChannel = new MessageChannel()

        messageChannel.port1.onmessage = (event) => {
          if (event.data.success) {
            resolve({
              active: true,
              timestamp: event.data.timestamp
            })
          } else {
            resolve({ active: false })
          }
        }

        registration.active.postMessage(
          { type: 'PING' },
          [messageChannel.port2]
        )

        // 타임아웃 설정 (3초)
        setTimeout(() => {
          resolve({ active: false })
        }, 3000)
      })
    } catch (error) {
      console.error('[Debug] Failed to ping service worker:', error)
      return { active: false }
    }
  }

  /**
   * Service Worker 상세 정보 조회
   */
  async getServiceWorkerInfo(): Promise<ServiceWorkerDebugInfo> {
    try {
      const registration = await navigator.serviceWorker.getRegistration()

      if (!registration) {
        return {
          isActive: false,
          updateFound: false
        }
      }

      const info: ServiceWorkerDebugInfo = {
        isActive: !!registration.active,
        scope: registration.scope,
        scriptURL: registration.active?.scriptURL,
        state: registration.active?.state,
        updateFound: !!registration.waiting || !!registration.installing
      }

      return info
    } catch (error) {
      console.error('[Debug] Failed to get service worker info:', error)
      return {
        isActive: false,
        updateFound: false
      }
    }
  }

  /**
   * 푸시 알림 권한 상태 확인
   */
  getNotificationPermission(): {
    permission: NotificationPermission
    isGranted: boolean
    isDenied: boolean
    isDefault: boolean
  } {
    const permission = Notification.permission

    return {
      permission,
      isGranted: permission === 'granted',
      isDenied: permission === 'denied',
      isDefault: permission === 'default'
    }
  }

  /**
   * 브라우저 푸시 지원 확인
   */
  checkPushSupport(): {
    serviceWorker: boolean
    pushManager: boolean
    notifications: boolean
    isFullySupported: boolean
  } {
    const serviceWorker = 'serviceWorker' in navigator
    const pushManager = 'PushManager' in window
    const notifications = 'Notification' in window

    return {
      serviceWorker,
      pushManager,
      notifications,
      isFullySupported: serviceWorker && pushManager && notifications
    }
  }

  /**
   * 종합 디버깅 정보 출력
   */
  async printDebugInfo(): Promise<void> {
    console.log('========================================')
    console.log('🔍 Service Worker Push Notification Debug Info')
    console.log('========================================')

    // 1. 브라우저 지원 확인
    const support = this.checkPushSupport()
    console.log('\n1️⃣ Browser Support:')
    console.log('  - Service Worker:', support.serviceWorker ? '✅' : '❌')
    console.log('  - Push Manager:', support.pushManager ? '✅' : '❌')
    console.log('  - Notifications:', support.notifications ? '✅' : '❌')
    console.log('  - Fully Supported:', support.isFullySupported ? '✅' : '❌')

    // 2. 알림 권한 확인
    const permission = this.getNotificationPermission()
    console.log('\n2️⃣ Notification Permission:')
    console.log('  - Status:', permission.permission)
    console.log('  - Granted:', permission.isGranted ? '✅' : '❌')
    console.log('  - Denied:', permission.isDenied ? '⚠️ YES' : 'NO')

    // 3. Service Worker 정보
    const swInfo = await this.getServiceWorkerInfo()
    console.log('\n3️⃣ Service Worker Info:')
    console.log('  - Active:', swInfo.isActive ? '✅' : '❌')
    console.log('  - Scope:', swInfo.scope)
    console.log('  - Script URL:', swInfo.scriptURL)
    console.log('  - State:', swInfo.state)
    console.log('  - Update Available:', swInfo.updateFound ? '⚠️ YES' : 'NO')

    // 4. Service Worker PING
    const ping = await this.pingServiceWorker()
    console.log('\n4️⃣ Service Worker Ping:')
    console.log('  - Responsive:', ping.active ? '✅' : '❌')
    console.log('  - Timestamp:', ping.timestamp || 'N/A')

    // 5. 푸시 수신 로그
    try {
      const logs = await this.getPushLogs()
      console.log('\n5️⃣ Push Receive Logs:')
      console.log('  - Total Logs:', logs.length)

      if (logs.length > 0) {
        console.log('  - Recent Logs:')
        logs.slice(-5).forEach((log, index) => {
          console.log(`\n  Log #${logs.length - 5 + index + 1}:`)
          console.log('    - Received At:', log.receivedAt)
          console.log('    - Has Data:', log.hasData ? '✅' : '❌')
          console.log('    - Parsed:', log.parsed ? '✅' : '❌')
          console.log('    - Title:', log.title || 'N/A')
          console.log('    - Displayed:', log.displayed ? '✅' : '❌')
          if (log.displayError) {
            console.log('    - Display Error:', log.displayError)
          }
          if (log.parseError) {
            console.log('    - Parse Error:', log.parseError)
          }
        })
      } else {
        console.log('  - No logs found. Send a test notification to generate logs.')
      }
    } catch (error) {
      console.error('  - Failed to get push logs:', error)
    }

    console.log('\n========================================')
    console.log('💡 Tip: Open DevTools > Application > Service Workers to see SW console logs')
    console.log('========================================\n')
  }
}

export const serviceWorkerDebugger = new ServiceWorkerDebugger()

// 전역 윈도우 객체에 디버거 추가 (개발자 콘솔에서 사용 가능)
if (typeof window !== 'undefined') {
  ;(window as any).swDebugger = serviceWorkerDebugger
  console.log('💡 Service Worker Debugger available: window.swDebugger.printDebugInfo()')
}